import { useCallback, useState } from 'react';
import type { DataTableRenderCellParams } from '@/components/custom/erp/data-table-v2';

export interface FeeRow {
  uuid?: string | null;
  tien_cp_nt?: number;
  [key: string]: MuaHangRecord;
}

export interface SelectedCellInfo {
  id: string;
  field: string;
}

export function useFeeRows(initialRows: FeeRow[] = []) {
  const [rows, setRows] = useState<FeeRow[]>(initialRows);
  const [selectedRowUuid, setSelectedRowUuid] = useState<string | null>(null);
  const [selectedRow, setSelectedRow] = useState<FeeRow | null>(null);
  const [selectedCell, setSelectedCell] = useState<SelectedCellInfo | null>(null);

  const handleAddRow = useCallback(() => {
    const newRow = {
      uuid: String(Math.random())
    };

    setRows(previousRows => [...previousRows, newRow]);

    setSelectedRowUuid(newRow.uuid);
    setSelectedRow(newRow);
  }, []);

  const handleDeleteRow = useCallback(() => {
    setRows(previousRows => {
      const updatedRows = selectedRowUuid
        ? previousRows.filter(row => row.uuid !== selectedRowUuid)
        : previousRows.slice(0, -1);

      if (updatedRows.length > 0) {
        const lastRow = updatedRows[updatedRows.length - 1];
        setSelectedRowUuid(lastRow.uuid || null);
        setSelectedRow(lastRow);
      } else {
        setSelectedRowUuid(null);
        setSelectedRow(null);
        setSelectedCell(null);
      }

      return updatedRows;
    });
  }, [selectedRowUuid]);

  const handleCopyRow = useCallback(() => {
    if (!selectedRow) return;

    const { uuid, ...rowDataWithoutUuid } = selectedRow;

    const newRow = {
      ...rowDataWithoutUuid,
      uuid: String(Math.random())
    };

    setRows(previousRows => [...previousRows, newRow]);

    setSelectedRowUuid(newRow.uuid);
    setSelectedRow(newRow);
  }, [selectedRow]);

  const handlePasteRow = useCallback(() => {
    if (!selectedRow) return;

    const { uuid, ...rowDataWithoutUuid } = selectedRow;

    let newRow = {
      ...rowDataWithoutUuid,
      uuid: String(Math.random())
    };

    setRows(previousRows => {
      if (!selectedRowUuid) return [...previousRows, newRow];

      const selectedIndex = previousRows.findIndex(row => row.uuid === selectedRowUuid);
      if (selectedIndex === -1) return previousRows;

      const newRows = [...previousRows];
      newRows.splice(selectedIndex + 1, 0, newRow);
      return newRows;
    });

    setSelectedRowUuid(newRow.uuid);
    setSelectedRow(newRow);
  }, [selectedRow, selectedRowUuid]);

  const handleMoveRow = useCallback(
    (direction: 'up' | 'down') => {
      if (!selectedRowUuid) return;

      setRows(previousRows => {
        const currentIndex = previousRows.findIndex(row => row.uuid === selectedRowUuid);
        if (currentIndex === -1) return previousRows;

        const newIndex =
          direction === 'up' ? Math.max(0, currentIndex - 1) : Math.min(previousRows.length - 1, currentIndex + 1);

        if (newIndex === currentIndex) return previousRows;

        const newRows = [...previousRows];
        const [movedRow] = newRows.splice(currentIndex, 1);
        newRows.splice(newIndex, 0, movedRow);

        setSelectedRow(movedRow);

        return newRows;
      });
    },
    [selectedRowUuid]
  );

  const handleCellValueChange = useCallback((rowUuid: string, field: string, newValue: MuaHangRecord) => {
    setRows(previousRows => {
      const rowIndex = previousRows.findIndex(row => row.uuid === rowUuid);
      if (rowIndex === -1) return previousRows;

      const currentRow = previousRows[rowIndex];
      if (Object.is(currentRow[field], newValue)) return previousRows;

      const updatedRow = {
        ...currentRow,
        [field]: newValue
      };
      const updatedRows = [...previousRows];
      updatedRows[rowIndex] = updatedRow;

      setSelectedRow(currentSelectedRow => (rowUuid === currentSelectedRow?.uuid ? updatedRow : currentSelectedRow));

      return updatedRows;
    });
  }, []);

  const handleRowClick = useCallback((params: { id: string; row: FeeRow }) => {
    const rowUuid = params.id || params.row?.uuid;
    if (!rowUuid) return;

    setSelectedRowUuid(rowUuid);
    setSelectedRow(params.row);
  }, []);

  const handleCellClick = useCallback((params: DataTableRenderCellParams<FeeRow>) => {
    const rowUuid = params.id.toString();
    if (!rowUuid) return;

    // Update row selection
    setSelectedRowUuid(rowUuid);
    setSelectedRow(params.row as FeeRow);

    // Update cell selection
    setSelectedCell({
      id: rowUuid,
      field: params.field
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedRowUuid(null);
    setSelectedRow(null);
    setSelectedCell(null);
  }, []);

  return {
    rows,
    setRows,
    selectedRowUuid,
    selectedRow,
    selectedCell,
    handleRowClick,
    handleCellClick,
    clearSelection,
    handleAddRow,
    handleDeleteRow,
    handleCopyRow,
    handlePasteRow,
    handleMoveRow,
    handleCellValueChange
  };
}
